import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TabsContent } from "@/components/ui/tabs";
import { JobFormData } from "./types/jobTypes";
import { useJobForm } from "./hooks/useJobForm";
import { BasicInformationTab } from "./form-tabs/BasicInformationTab";
import { JobDetailsTab } from "./form-tabs/JobDetailsTab";
import { QualificationsTab } from "./form-tabs/QualificationsTab";
import { AdditionalInfoTab } from "./form-tabs/AdditionalInfoTab";
import { FormTabs } from "./form-tabs/FormTabs";
import { WizardTimeline } from "./WizardTimeline";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";

interface JobOffersAddWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onAddJob: (jobData: any) => Promise<string | undefined>;
  initialJobData?: Partial<JobFormData>;
}

export const JobOffersAddWizard = ({
  isOpen,
  onClose,
  onAddJob,
  initialJobData = {}
}: JobOffersAddWizardProps) => {
  const { t } = useTranslation();
  
  // Using the same job form hook as the edit modal
  const {
    jobData,
    setJobData,
    isSubmitting,
    setIsSubmitting,
    activeTab,
    setActiveTab,
    selectedLanguages,
    showOtherLanguageField,
    showBudgetPerm,
    showBudgetFreelance,
    handleSelectCompany,
    handleSelectContact,
    handleSkillsKeyDown,
    handleLanguageChange,
    handleEmploymentTypeChange,
    handleTimeCommitmentChange,
    handleInputChange,
    handleSubmit,
    handlePasteFromClipboard,
    isEmploymentTypeSelected,
    resetForm
  } = useJobForm(undefined, async (data) => {
    // Create mode - no job ID needed
    try {
      console.log("Preparing job data for submission:", data);
      // Process the form data before sending it to the API
      const processedData = {
        // Basic information
        title: data.title,
        companyId: data.companyId,
        companyName: data.companyName,
        description: data.description,
        skills: data.skills ? data.skills.split(',').map(skill => skill.trim()) : [],
        contactId: data.contactId,
        aiSummary: data.aiSummary || "",
        
        // Process mapped field names for the API
        employment_type: data.employmentType,
        time_commitment: data.timeCommitment,
        contract_duration: data.contractDuration,
        start_date: data.startDate,
        end_date: data.endDate,
        seniority_level: data.seniorityLevel,
        budget: data.budget,
        budget_freelance: data.budgetFreelance,
        location_type: data.locationType,
        workplace_breakdown: data.workplaceBreakdown,
        hard_skills: data.hardSkills ? data.hardSkills.split(',').map(skill => skill.trim()) : [],
        soft_skills: data.softSkills ? data.softSkills.split(',').map(skill => skill.trim()) : [],
        education_level: data.educationLevel,
        years_of_experience: data.yearsOfExperience,
        language_requirements: data.languageRequirements,
        extra_lang: data.extraLang,
        culture_tags: data.cultureTags ? data.cultureTags.split(',').map(tag => tag.trim()) : [],
        application_deadline: data.applicationDeadline,
        job_posting_date: data.jobPostingDate,
        source: data.source,
        assigned_recruiter: data.assignedRecruiter,
        employment_classification: data.employmentClassification,
        ai_summary: data.aiSummary, // Explicitly map aiSummary to both fields for compatibility
        notes: data.notes
      };
      
      console.log("Processed data for API:", processedData);
      const result = await onAddJob(processedData);
      
      if (result) {
        console.log("Job created successfully with ID:", result);
        return true;
      } else {
        console.error("Job creation failed - no ID returned");
        return false;
      }
    } catch (error) {
      console.error("Error in job creation:", error);
      throw error;
    }
  });

  // Apply initial job data when component mounts or initialJobData changes
  useEffect(() => {
    if (isOpen && initialJobData && Object.keys(initialJobData).length > 0) {
      console.log("Setting initial job data:", initialJobData);
      setJobData(prevData => ({
        ...prevData,
        ...initialJobData
      }));
    }
  }, [isOpen, initialJobData, setJobData]);

  // Custom form submit handler for adding a new job
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("JobOffersAddWizard handleFormSubmit called");
    
    // Only submit if it's an explicit save or last step submission 
    let isExplicitSave = false
    let isSubmitButtonClick = false;
    
    if (e.nativeEvent?._isSubmitButtonClick) {
      isSubmitButtonClick = true;
    }

    if (e.nativeEvent?._isExplicitSave)
      isExplicitSave = true;
    
    console.log("Form submission flags:", { isExplicitSave, isSubmitButtonClick });
    
    if (isExplicitSave || isSubmitButtonClick) {
      try {
        setIsSubmitting(true);
        console.log("Submitting form data:", jobData);
        const success = await handleSubmit(e);
        console.log("Form submission result:", success);
        
        if (success) {
          toast({
            title: t("common.success"),
            description: t("jobs.formWizard.createSuccess")
          });
          onClose();
          return;
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast({
          title: t("common.error"),
          description: t("jobs.formWizard.createError") + (error instanceof Error ? ": " + error.message : ""),
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log("Not submitting form - not triggered by save or submit");
    }
  };

  const steps = [
    t("jobs.formWizard.chooseMethod"), 
    t("jobs.formWizard.basicInfo"), 
    t("jobs.formWizard.jobDetails"), 
    t("jobs.formWizard.qualifications"), 
    t("jobs.formWizard.additionalInfo")
  ];
  
  const stepIndex = {
    "basic": 1,
    "details": 2,
    "qualifications": 3,
    "metadata": 4
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow closing via the explicit onClose handler
      if (!open) onClose();
    }}>
      <DialogContent 
        className="w-[95vw] max-w-[95vw] max-h-[90vh] h-[90vh] flex flex-col p-0 overflow-hidden" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <ProcessingOverlay 
          isProcessing={isSubmitting}
          message="Creating job offer..."
        />
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-semibold">{t("jobs.formWizard.title")}</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pt-4 flex-grow flex flex-col overflow-hidden">
          <WizardTimeline 
            steps={steps} 
            currentStep={stepIndex[activeTab as keyof typeof stepIndex]} 
          />
          
          <div className="mt-6 flex-grow overflow-hidden">
            <FormTabs 
              activeTab={activeTab} 
              setActiveTab={setActiveTab}
              onCancel={onClose}
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
              isCreateMode={true}
            >
              <TabsContent value="basic" className="h-full">
                <BasicInformationTab 
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                  handleSelectCompany={handleSelectCompany}
                  handleSelectContact={handleSelectContact}
                  handlePasteFromClipboard={handlePasteFromClipboard}
                  handleSkillsKeyDown={handleSkillsKeyDown}
                  handleEmploymentTypeChange={handleEmploymentTypeChange}
                  isEmploymentTypeSelected={isEmploymentTypeSelected}
                />
              </TabsContent>
              
              <TabsContent value="details" className="h-full">
                <JobDetailsTab
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                  handleSkillsKeyDown={handleSkillsKeyDown}
                  handleEmploymentTypeChange={handleEmploymentTypeChange}
                  handleTimeCommitmentChange={handleTimeCommitmentChange}
                  isEmploymentTypeSelected={isEmploymentTypeSelected}
                  showBudgetPerm={showBudgetPerm}
                  showBudgetFreelance={showBudgetFreelance}
                />
              </TabsContent>
              
              <TabsContent value="qualifications" className="h-full">
                <QualificationsTab
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                  handleSkillsKeyDown={handleSkillsKeyDown}
                  handleLanguageChange={handleLanguageChange}
                  selectedLanguages={selectedLanguages}
                  showOtherLanguageField={showOtherLanguageField}
                />
              </TabsContent>
              
              <TabsContent value="metadata" className="h-full">
                <AdditionalInfoTab
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                />
              </TabsContent>
            </FormTabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
